import * as Sentry from '@sentry/browser'
import React, { useContext, useEffect, useRef } from 'react'
import isObject from 'lodash/isObject.js'

// uncomment this line to enable Sentry in dev mode
let DISABLE_SENTRY = process.env.REACT_APP_ENV === 'development'

let Context = React.createContext(false)

export let useErrorBoundary = () => useContext(Context)

export function useOnError(callback) {
  let [error] = useErrorBoundary()
  let didMount = useRef(false)

  useEffect(() => {
    if (!didMount.current) {
      didMount.current = true
      return
    }

    if (error) {
      callback()
    }
  }, [error, callback])
}

let filterSensitiveData = makeRedact({
  properties: [
    // from lib
    'apikey',
    'api-key',
    'api_key',
    'pass',
    'password',
    'secret',
    'x-api-key',
  ],
  text: '[...]',
})

export function captureError(error, rawInfo = {}) {
  requestAnimationFrame(() => {
    let info = Object.keys(rawInfo).length > 0 && filterSensitiveData(rawInfo)
    if (DISABLE_SENTRY) {
      console.error(error)
      console.log(info)
      return
    }

    if (info) {
      info.embedUrl = {
        domain: document.domain,
        origin: document.location.origin,
        // removed it's throwing this issue https://sentry.io/organizations/greyfinch/issues/1161172413/?project=1392628&query=is%3Aunresolved
        // parent: window.parent.location.origin,
      }

      Sentry.withScope(scope => {
        Object.keys(info).forEach(key => scope.setExtra(key, info[key]))
        if (error?.response?.headers?.has('x-request-id')) {
          scope.setExtra(
            'x-request-id',
            error.response.headers.get('x-request-id')
          )
        }
        Sentry.captureException(error)
      })
    } else {
      Sentry.captureException(error)
    }
  })
}

export function captureBreadcrumb({
  category,
  message,
  data,
  level = 'debug', // https://github.com/getsentry/sentry-javascript/blob/7.0.0/MIGRATION.md#severity-severitylevel-and-severitylevels
}) {
  if (process.env.REACT_APP_ENV === 'development') {
    console.debug({
      type: 'error-boundary/breadcrumb',
      category,
      message,
      data,
      level,
    })
  }

  if (DISABLE_SENTRY) return

  if (category === 'auth' && data?.id) {
    Sentry.configureScope(function (scope) {
      try {
        scope.setUser(data)
      } catch (error) {}
    })
  }

  Sentry.addBreadcrumb({
    category,
    message,
    data,
    level,
  })
}

export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = { error: false }

    this.toggleErrorOff = () => {
      this.setState(() => ({ error: false }))
    }
  }

  componentDidMount() {
    // uncomment this line to enable Sentry in dev mode
    if (DISABLE_SENTRY) return

    Sentry.init({
      dsn: process.env.REACT_APP_SENTRY_KEY,
      environment: process.env.REACT_APP_ENV,
      release: `${process.env.REACT_APP_NAME}-${process.env.REACT_APP_VERSION}`,
      ignoreErrors: [
        'Could not verify JWT',
        'Failed to fetch dynamically imported module',
        'Importing a module script failed',
        'Invalid token',
        'Invalid username or password',
        'Loading chunk',
        'no subscriptions exist',
        'Non-Error exception captured',
        'Non-Error promise rejection captured',
        'onion',
        'pixie',
        'requestAnimationFrame is not defined',
        'ResizeObserver loop completed with undelivered notifications',
        'ResizeObserver loop limit exceeded',
        `not found in type: 'query_root`,
        `Can't find variable: fetch`,
        `Cannot read properties of undefined (reading 'getBoundingClientRect')`, // user pilot
      ],
      // By Default is 3
      normalizeDepth: 6,
    })
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ error: true })

    captureError(error, errorInfo)
  }

  render() {
    return (
      <Context.Provider value={[this.state.error, this.toggleErrorOff]}>
        {this.props.children}
      </Context.Provider>
    )
  }
}

let VISIBLE_PADDING = 3 // characters

// src https://github.com/rcjpisani/redactyl.js/blob/master/src/index.js
function makeRedact({ properties, text }) {
  let regex = new RegExp(`(${properties.join('|')})`, 'i')

  return function redact(json) {
    if (!isObject(json)) {
      throw new TypeError('A valid JSON object must be specified')
    }

    let redacted = JSON.parse(JSON.stringify(json))

    Object.entries(redacted).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.filter(isObject).forEach((item, index) => {
          redacted[key][index] = redact(item)
        })
      } else if (isObject(value)) {
        redacted[key] = redact(value)
      } else if (regex.test(key)) {
        redacted[key] = text
      } else if (typeof value === 'string' && regex.test(value)) {
        // this catches values like https://site.com/?token=1321321
        let match = value.match(regex)[1]
        redacted[key] = `${value.slice(
          0,
          value.indexOf(match) + match.length + VISIBLE_PADDING
        )}${text}`
      }
    })

    return redacted
  }
}
